import { Schema } from "effect";
import {
  AccountId,
  ChannelThreadId,
  ConversationId,
  MessageId,
  SupportTicketId,
} from "../../../ids/definitions";
import { MongoCustomerId, MongoUserId } from "../../../ids/mongo-ids";
import { Attachment } from "./message/attachment";
import {
  ContactFormChannelData,
  FacebookCommentChannelData,
  FacebookDmChannelData,
  GmailChannelData,
  InstagramCommentChannelData,
  InstagramDmChannelData,
  InternalNoteChannelData,
  MobileMessageChannelData,
  OutlookChannelData,
  RedoChatChannelData,
  SmsChannelData,
  VoiceChannelData,
} from "./message/channel-data";
import { ConversationProduct } from "./product";

export const MESSAGE_CHANNELS = [
  "gmail",
  "outlook",
  "redoChat",
  "facebookDm",
  "facebookComments",
  "instagramDm",
  "instagramComments",
  "sms",
  "voice",
  "attentive",
  "postscript",
  "tiktok",
  "contactForm",
  "internalNote",
  "mobileMessage",
] as const;

export const MessageChannel = Schema.Literal(...MESSAGE_CHANNELS);
export type MessageChannel = typeof MessageChannel.Type;

export const MessageOrigin = Schema.Literal("external", "internal");
export type MessageOrigin = typeof MessageOrigin.Type;

export const MessageSenderType = Schema.Literal(
  "merchant",
  "customer",
  "system",
  "ai",
  "auto_reply",
  "chat_flow",
);
export type MessageSenderType = typeof MessageSenderType.Type;

// Base fields shared by all message variants
const BaseMessageFields = {
  accountId: AccountId,
  content: Schema.NullOr(Schema.String),
  channelThreadId: ChannelThreadId,
  id: MessageId,
  inReplyToId: Schema.NullOr(MessageId),
  inReplyToExternalId: Schema.NullOr(Schema.String),
  origin: MessageOrigin,
  products: Schema.Array(ConversationProduct),
  senderType: MessageSenderType,
  senderMongoCustomerId: Schema.NullOr(MongoCustomerId),
  senderMongoUserId: Schema.NullOr(MongoUserId),
  sentAt: Schema.Date,
  attachments: Schema.Array(Attachment),
  externalMessageId: Schema.String,
};

// Channel-specific message variants
const GmailMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("gmail"),
  channelData: Schema.NullOr(GmailChannelData),
});

const OutlookMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("outlook"),
  channelData: Schema.NullOr(OutlookChannelData),
});

const SmsMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("sms"),
  channelData: Schema.NullOr(SmsChannelData),
});

const MobileMessageMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("mobileMessage"),
  channelData: Schema.NullOr(MobileMessageChannelData),
});

const RedoChatMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("redoChat"),
  channelData: Schema.NullOr(RedoChatChannelData),
});

const VoiceMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("voice"),
  channelData: Schema.NullOr(VoiceChannelData),
});

// Facebook DMs use channel="facebookDm" with FacebookDmChannelData
const FacebookMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("facebookDm"),
  channelData: Schema.NullOr(FacebookDmChannelData),
});

// Facebook comments use channel="facebookComments" with FacebookCommentChannelData
const FacebookCommentsMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("facebookComments"),
  channelData: Schema.NullOr(FacebookCommentChannelData),
});

// Instagram DMs use channel="instagramDm" with InstagramDmChannelData
const InstagramMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("instagramDm"),
  channelData: Schema.NullOr(InstagramDmChannelData),
});

// Instagram comments use channel="instagramComments" with InstagramCommentChannelData
const InstagramCommentsMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("instagramComments"),
  channelData: Schema.NullOr(InstagramCommentChannelData),
});

// Channels without specific channelData (always null)
const AttentiveMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("attentive"),
  channelData: Schema.Null,
});

const PostscriptMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("postscript"),
  channelData: Schema.Null,
});

const TiktokMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("tiktok"),
  channelData: Schema.Null,
});

const ContactFormMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("contactForm"),
  channelData: Schema.NullOr(ContactFormChannelData),
});

const InternalNoteMessage = Schema.Struct({
  ...BaseMessageFields,
  channel: Schema.Literal("internalNote"),
  channelData: Schema.NullOr(InternalNoteChannelData),
});

// Discriminated union on channel
export const Message = Schema.Union(
  GmailMessage,
  OutlookMessage,
  SmsMessage,
  MobileMessageMessage,
  RedoChatMessage,
  VoiceMessage,
  FacebookMessage,
  FacebookCommentsMessage,
  InstagramMessage,
  InstagramCommentsMessage,
  AttentiveMessage,
  PostscriptMessage,
  TiktokMessage,
  ContactFormMessage,
  InternalNoteMessage,
);
export type Message = typeof Message.Type;

export const MessageCursor = Schema.Struct({
  sentAt: Schema.DateFromString,
  id: MessageId,
});
export type MessageCursor = typeof MessageCursor.Type;

export type MessageWithConversationId = Message & {
  conversationId?: ConversationId;
};

export type MessageWithSupportTicketId = Message & {
  supportTicketId?: SupportTicketId;
};
